import React from 'react';

const ReportCardPDF = ({ id, student, examName, results, examSetup }) => {
  if (!student || !results || results.length === 0) return null;

  // Resolve each result's exam-setup entry once, and split into mark-based
  // vs. grade-only subjects so they can render as two separate tables.
  const enrichedResults = results.map(r => {
    let setupSubject = null;
    if (examSetup && examSetup.schedule && student.className && examSetup.schedule[student.className]) {
      setupSubject = examSetup.schedule[student.className].find(s => s.subjectName === (r.subject || r.subjectName));
    }
    return { ...r, setupSubject, isGradeOnly: !!setupSubject?.isGradeOnly };
  });
  const markSubjects = enrichedResults.filter(r => !r.isGradeOnly);
  const gradeSubjects = enrichedResults.filter(r => r.isGradeOnly);

  // Calculate totals (grade-only subjects are excluded from marks totals)
  let totalMax = 0;
  let totalMin = 0;
  let totalGained = 0;
  let allPassed = true;

  markSubjects.forEach(({ setupSubject, ...r }) => {
    const max = setupSubject ? Number(setupSubject.totalMarks) : (Number(r.totalMarks) || 100);
    const min = setupSubject ? Number(setupSubject.minMarks) : (Number(r.minMarks) || 35);

    totalMax += max;
    totalMin += min;

    if (r.marks === 'AB') {
      allPassed = false;
    } else {
      const marks = Number(r.marks) || 0;
      totalGained += marks;
      if (marks < min) {
        allPassed = false;
      }
    }
  });

  const percentage = totalMax > 0 ? ((totalGained / totalMax) * 100).toFixed(2) : 0;

  // Scale both tables down together as total subject count grows, so a long
  // grade-subjects table can never push past the footer/signature area.
  // Thresholds sit lower than a single-table layout would need, since
  // splitting into two tables adds an extra header row + label.
  const total = results.length;
  const SIZE = total > 16
    ? { headH: 'h-6', headPad: 'p-0.5', headText: 'text-[9px]',  rowH: 'h-5', pad: 'p-0.5', text: 'text-[9px]',  labelText: 'text-[10px]', labelM: 'mt-0.5 mb-0.5' }
    : total > 12
    ? { headH: 'h-7', headPad: 'p-1',   headText: 'text-[10px]', rowH: 'h-6', pad: 'p-0.5', text: 'text-[11px]', labelText: 'text-[11px]', labelM: 'mt-1 mb-0.5' }
    : total > 8
    ? { headH: 'h-8', headPad: 'p-1',   headText: 'text-[11px]', rowH: 'h-8', pad: 'p-1',   text: 'text-xs',     labelText: 'text-xs',     labelM: 'mt-1.5 mb-1' }
    : { headH: 'h-12', headPad: 'p-2',  headText: 'text-[13px]', rowH: 'h-10', pad: 'p-2',  text: 'text-sm',     labelText: 'text-sm',     labelM: 'mt-2 mb-1' };

  return (
    <div 
      id={id} 
      className="bg-white w-[794px] h-[1123px] p-6 text-black absolute top-[-9999px] left-[-9999px]"
      style={{ fontFamily: "'Times New Roman', serif" }}
    >
      <div className="border-[10px] border-red-700 w-full h-full p-4 flex flex-col relative">

        {/* Header */}
        <div className="border-b-[3px] border-black pb-2 mb-2">
           <img src="/report_header.png" alt="St. Paul's High School" className="w-full object-contain" crossOrigin="anonymous" />
        </div>

        {/* Title */}
        <div className="text-center mb-1">
          <h2 className="text-[2rem] font-bold text-[#000080] tracking-wider mb-1">ACHIEVEMENT RECORD</h2>
          <div className="flex items-center justify-center">
            <div className="h-[2px] w-[150px] bg-[#000080]"></div>
            <span className="px-4 text-[#000080] font-bold text-xl">{student.academicYear || '2024-25'}</span>
            <div className="h-[2px] w-[150px] bg-[#000080]"></div>
          </div>
        </div>

        {/* STUDENT DETAILS section title */}
        <div className="flex w-full mb-2 ml-2">
          <div className="bg-black text-white px-4 py-1.5 font-bold tracking-widest text-sm">
            STUDENT DETAILS
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[1.1rem] font-bold text-[#000080] pl-2 mb-4">
          <div>Student Name: <span className="capitalize">{student.name || student.fullName}</span></div>
          <div>Class: {student.className}</div>
          <div>Section: {student.section}</div>
          <div>Father Name: <span className="capitalize">{student.fatherName || student.parentName || '-'}</span></div>
        </div>

        {/* Exam Name */}
        <div className="flex justify-center mb-2">
          <div className="bg-[#b31b1b] text-white px-10 py-1.5 text-2xl font-bold uppercase rounded shadow-sm tracking-wider">
            {examName}
          </div>
        </div>

        {/* Marks-based subjects table */}
        {markSubjects.length > 0 && (
          <table className="w-full border-collapse border border-gray-400 mb-1 text-center mt-1">
            <thead>
              <tr className={`border border-gray-400 ${SIZE.headH}`}>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold w-[25%]`}>SUBJECT :</th>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold`}>MAX.MARKS</th>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold`}>MIN.MARKS</th>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold`}>MARKS GAINED</th>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold w-[25%]`}>REMARKS ( PASS/FAIL)</th>
              </tr>
            </thead>
            <tbody>
              {markSubjects.map(r => {
                const { setupSubject } = r;
                const max = setupSubject ? Number(setupSubject.totalMarks) : (Number(r.totalMarks) || 100);
                const min = setupSubject ? Number(setupSubject.minMarks) : (Number(r.minMarks) || 35);
                const isAbsent = r.marks === 'AB';
                const marks = isAbsent ? 0 : Number(r.marks) || 0;
                const isPass = !isAbsent && (marks >= min);

                return (
                  <tr key={r.id || r.subject} className={`border border-gray-400 ${SIZE.rowH}`}>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-bold text-left pl-4 uppercase`}>{r.subject || r.subjectName}</td>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-semibold`}>{max}</td>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-semibold`}>{min}</td>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-bold`}>{isAbsent ? 'AB' : marks}</td>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-bold`}>{isPass ? 'PASS' : 'FAIL'}</td>
                  </tr>
                );
              })}
              <tr className={`border border-gray-400 ${SIZE.rowH} font-bold ${SIZE.text}`}>
                <td className={`border border-gray-400 ${SIZE.pad} text-left pl-4`}>TOTAL</td>
                <td className={`border border-gray-400 ${SIZE.pad}`}>{totalMax}</td>
                <td className={`border border-gray-400 ${SIZE.pad}`}></td>
                <td className={`border border-gray-400 ${SIZE.pad}`}>{totalGained}</td>
                <td className={`border border-gray-400 ${SIZE.pad}`}></td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Grade-only subjects table */}
        {gradeSubjects.length > 0 && (
          <>
          <div className={`text-left font-bold text-[#000080] ${SIZE.labelText} ${SIZE.labelM} pl-1`}>GRADE SUBJECTS</div>
          <table className="w-full border-collapse border border-gray-400 mb-1 text-center">
            <thead>
              <tr className={`border border-gray-400 ${SIZE.headH}`}>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold w-[75%]`}>SUBJECT :</th>
                <th className={`border border-gray-400 ${SIZE.headPad} ${SIZE.headText} font-bold w-[25%]`}>GRADE</th>
              </tr>
            </thead>
            <tbody>
              {gradeSubjects.map(r => {
                const isAbsent = r.grade === 'AB' || r.marks === 'AB';
                return (
                  <tr key={r.id || r.subject} className={`border border-gray-400 ${SIZE.rowH}`}>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-bold text-left pl-4 uppercase`}>{r.subject || r.subjectName}</td>
                    <td className={`border border-gray-400 ${SIZE.pad} ${SIZE.text} font-bold`}>{isAbsent ? 'AB' : (r.grade || '-')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}

        {/* Footer (Percentage and Stamp) */}
        <div className="flex justify-between items-center px-6 mt-4 relative z-10">
          <div className="text-xl font-bold flex items-end gap-2">
            <span className="mb-1">Percentage :</span>
            <span className="border-b-2 border-black inline-block min-w-[120px] text-center pb-0.5">{percentage}%</span>
          </div>
          
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[-10px] z-0">
             {/* Stamp */}
             {allPassed ? (
               <div className="border-[5px] border-[#2e7d32] text-[#2e7d32] rounded-full w-[90px] h-[90px] flex items-center justify-center -rotate-12 font-black text-lg tracking-widest opacity-80 border-double bg-white">
                  PASSED
               </div>
             ) : (
               <div className="border-[5px] border-[#d32f2f] text-[#d32f2f] rounded-full w-[90px] h-[90px] flex items-center justify-center -rotate-12 font-black text-lg tracking-widest opacity-80 border-double bg-white">
                  FAILED
               </div>
             )}
          </div>
        </div>

        {/* Spacer to push signatures to bottom but above border */}
        <div className="flex-1 min-h-[5px]"></div>

        {/* Signatures */}
        <div className="flex justify-between items-end mt-1 mb-1 px-10 font-bold text-xl relative z-20">
          <div>Parent Signature</div>
          <div className="flex flex-col items-center">
            <img src="/principal_signature.jpg" alt="Principal Signature" className="h-16 mb-1 object-contain mix-blend-multiply" crossOrigin="anonymous" />
            <div>Principal Signature</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCardPDF;
